import { interleaveStopRowKeys, jobRowKey, parseRowKey, type RowKey } from "./stopPlanning";

/** What one shift's sequence after a save is built from. */
interface ShiftSaveSequence {
	/** The shift's jobs once the save lands, in their jobs-only order. */
	membership: readonly string[];
	/** The staged mixed sequence, if the card was opened. It may still hold rows the save drops. */
	staged: readonly RowKey[] | undefined;
	/** The shift's committed stops. */
	committedStopIds: readonly string[];
	/** Committed stops staged for deletion. */
	removedStopIds: ReadonlySet<string>;
	/** Current `Jobs.Order` per job. */
	knownOrderByJobId: ReadonlyMap<string, number | undefined>;
	/** Current `ShiftDropoffs.Order` per committed stop. */
	knownStopOrderByStopId: ReadonlyMap<string, number | undefined>;
}

/**
 * A shift's final mixed sequence once a save lands. The staged row-key list is the ordering
 * authority; rows that didn't survive the save are dropped: job keys outside the shift's final
 * membership (removal-staged / moved away), repeated job keys, and removal-staged stop keys. A
 * member the staged list never placed is appended.
 *
 * With no staged list (the card was never opened) the shift may still carry committed stops, so
 * its committed sequence is reconstructed and their slots survive; a jobs-only fallback would
 * drop the stop and collide a job onto its Order. A stop-free shift reduces to its jobs list.
 */
export function finalRowKeysForShift({
	membership,
	staged,
	committedStopIds,
	removedStopIds,
	knownOrderByJobId,
	knownStopOrderByStopId,
}: ShiftSaveSequence): RowKey[] {
	if (!staged) {
		const stops = committedStopIds
			.filter((id) => !removedStopIds.has(id))
			.map((id) => ({ id, order: knownStopOrderByStopId.get(id) }));
		return interleaveStopRowKeys(membership, knownOrderByJobId, stops);
	}
	const memberSet = new Set(membership);
	const kept: RowKey[] = [];
	const placedJobs = new Set<string>();
	for (const key of staged) {
		const parsed = parseRowKey(key);
		if (parsed.kind === "job") {
			if (!memberSet.has(parsed.id) || placedJobs.has(parsed.id)) continue;
			placedJobs.add(parsed.id);
			kept.push(jobRowKey(parsed.id));
		} else if (parsed.kind === "stop") {
			if (!removedStopIds.has(parsed.id)) kept.push(key);
		} else {
			kept.push(key);
		}
	}
	for (const id of membership) if (!placedJobs.has(id)) kept.push(jobRowKey(id));
	return kept;
}
